import {
  ReviewRatingMap,
  type Conversation,
  type ConversationPost,
  type LinkedInPost,
} from "../../../types/conversationInbox";

import type {
  InboxV2Account,
  InboxV2ConversationSourceContext,
  InboxV2MessageType,
} from "../../types";

import { mapOptionalInboxV2Identity } from "./interlocutor";

type SourceContextConversation = Pick<
  Conversation,
  "account" | "post" | "question" | "review"
>;

export function buildSourceContext(
  conversation: SourceContextConversation,
  messageType: Exclude<InboxV2MessageType, "all">,
  account?: InboxV2Account
): InboxV2ConversationSourceContext | undefined {
  if (messageType === "dm") {
    return undefined;
  }

  if (conversation.review) {
    const owner = mapOptionalInboxV2Identity(conversation.review.owner, {
      account,
    });

    return {
      authorId: owner?.id,
      authorName: owner?.name,
      authorHandle: owner?.handle,
      authorIsAccount: owner?.isConversationAccountSender,
      authorPlatform: owner?.platform,
      authorPlatformId: owner?.platformId,
      authorFetchedError: owner?.fetchedError,
      authorIsUnknownSource: owner?.isUnknownSource,
      body: conversation.review.comment,
      createdAt: conversation.review.createdAt,
      mediaUrl: owner?.avatarUrl || undefined,
      rating: ReviewRatingMap[conversation.review.rating],
    };
  }

  if (conversation.question) {
    const owner = mapOptionalInboxV2Identity(conversation.question.owner, {
      account,
    });

    return {
      authorId: owner?.id,
      authorName: owner?.name,
      authorHandle: owner?.handle,
      authorIsAccount: owner?.isConversationAccountSender,
      authorPlatform: owner?.platform,
      authorPlatformId: owner?.platformId,
      authorFetchedError: owner?.fetchedError,
      authorIsUnknownSource: owner?.isUnknownSource,
      body: conversation.question.comment || conversation.question.text,
      createdAt: conversation.question.createdAt,
      mediaUrl: owner?.avatarUrl || undefined,
      questionText: conversation.question.text,
    };
  }

  if (conversation.post) {
    const owner = mapOptionalInboxV2Identity(conversation.post.owner, {
      account,
    });
    const postUrl = getSourcePostUrl(conversation.post);

    return {
      postId: conversation.post.id,
      postPlatformId: conversation.post.platformId || undefined,
      previewIn: mapPreviewIn(messageType),
      authorId: owner?.id,
      authorName: owner?.name,
      authorHandle: owner?.handle,
      authorIsAccount: owner?.isConversationAccountSender,
      authorPlatform: owner?.platform,
      authorPlatformId: owner?.platformId,
      authorFetchedError: owner?.fetchedError,
      authorIsUnknownSource: owner?.isUnknownSource,
      body: normalizeSourcePostBody(
        conversation.post.caption || conversation.post.text || "",
        conversation.post
      ),
      createdAt: conversation.post.createdAt,
      mediaAssetUrl:
        conversation.post.mediaUrl || conversation.post.thumbnail || undefined,
      mediaType: conversation.post.mediaType,
      mediaUrl: getSourcePostMediaUrl(conversation.post) || undefined,
      thumbnailUrl: conversation.post.thumbnail || undefined,
      postPreview: buildSourcePostPreview(conversation.post, postUrl),
      url: postUrl,
    };
  }

  return undefined;
}

function normalizeSourcePostBody(body: string, post: ConversationPost) {
  if (post.platform !== "LI") {
    return body;
  }

  return body
    .replace(/\{hashtag\|\\?#\|([^}]+)\}/g, "#$1")
    .replace(/\\([()])/g, "$1")
    .trim();
}

function getSourcePostMediaUrl(post: ConversationPost) {
  const resolvedContent = (post as LinkedInPost).resolvedContent;

  if (resolvedContent?.type === "image") {
    return resolvedContent.url;
  }

  if (
    resolvedContent?.type === "images" ||
    resolvedContent?.type === "carousel"
  ) {
    return resolvedContent.images[0]?.url;
  }

  if (resolvedContent?.type === "video") {
    return post.thumbnail || post.mediaUrl || resolvedContent.url;
  }

  return post.thumbnail || post.mediaUrl;
}

function getSourcePostUrl(post: ConversationPost) {
  if (post.url) {
    return post.url;
  }

  if (post.platform === "FB" && post.platformId?.trim()) {
    return `https://facebook.com/${post.platformId.trim()}`;
  }

  if (post.platform === "LI" && post.platformId) {
    return `https://linkedin.com/feed/update/${post.platformId}`;
  }

  return undefined;
}

function buildSourcePostPreview(
  post: ConversationPost,
  postUrl?: string
): InboxV2ConversationSourceContext["postPreview"] {
  const linkedInPost = post as LinkedInPost;
  const article = linkedInPost.content?.article;

  if (article) {
    return {
      kind: "article",
      description: article.description,
      source: article.source,
      thumbnailUrl:
        post.thumbnail ||
        post.mediaUrl ||
        getLinkedInResolvedContentFirstImageUrl(linkedInPost),
      title: article.title,
      url: postUrl,
    };
  }

  const resolvedContent = linkedInPost.resolvedContent;

  if (resolvedContent?.type === "video") {
    return {
      kind: "video",
      expiredAt: resolvedContent.expiredAt,
      title: linkedInPost.content?.media?.title,
      url: resolvedContent.url,
    };
  }

  if (
    resolvedContent?.type === "images" ||
    resolvedContent?.type === "carousel"
  ) {
    return {
      kind: "images",
      images: resolvedContent.images.map((image) => ({
        expiredAt: image.expiredAt,
        id: (image as { id?: string }).id,
        url: image.url,
      })),
      url: postUrl,
    };
  }

  if (resolvedContent?.type === "image") {
    return {
      kind: "images",
      images: [
        {
          expiredAt: resolvedContent.expiredAt,
          url: resolvedContent.url,
        },
      ],
      url: postUrl,
    };
  }

  if (post.mediaType === "VIDEO" && !post.thumbnail && post.mediaUrl) {
    return {
      kind: "video",
      url: post.mediaUrl,
    };
  }

  return undefined;
}

function getLinkedInResolvedContentFirstImageUrl(post: LinkedInPost) {
  const resolvedContent = post.resolvedContent;

  if (resolvedContent?.type === "image") {
    return resolvedContent.url;
  }

  if (
    resolvedContent?.type === "images" ||
    resolvedContent?.type === "carousel"
  ) {
    return resolvedContent.images[0]?.url;
  }

  return undefined;
}

function mapPreviewIn(
  messageType: Exclude<InboxV2MessageType, "all">
): InboxV2ConversationSourceContext["previewIn"] {
  if (messageType === "comment" || messageType === "threaded-comment") {
    return "comment";
  }

  if (messageType === "mention") {
    return "mention-in-comment";
  }

  if (messageType === "mention-in-caption") {
    return "mention-in-caption";
  }

  if (messageType === "post") {
    return "new-post-on-page";
  }

  if (messageType === "tagged-post" || messageType === "tagged-in-media") {
    return "tagged-in-post";
  }

  return undefined;
}
